import PropTypes from "prop-types"
import { useEffect, useContext } from "react"
import { useParams } from "react-router-dom"

import GenericNotFound from "app/generic-not-found"
import Loading from "areas/helpdesk/loading"
import { HelpdeskContext, fetchArticleResults } from "contexts/helpdesk"

/**
 * High Order Component for fetching helpdesk article results
 * @param {React Component} WrappedComponent
 */
const fetchArticle = (WrappedComponent) => (props) => {
  const { state, dispatch } = useContext(HelpdeskContext)
  const { article_id } = useParams()

  const InternalFetchArticle = ({
    article,
    fetchArticleResults,
    helpdeskArticle,
    helpdeskRelatedArticles,
    staticContext,
    ...props
  }) => {
    // Determine initial values to display
    let initialValues
    if (helpdeskArticle) {
      initialValues = {
        article: helpdeskArticle,
        related_articles: helpdeskRelatedArticles,
      }
    } else {
      initialValues = { article: {}, related_articles: [] }
    }
    let results = initialValues

    // Handle SPA behaviour of navigating to articles
    useEffect(() => {
      if (!helpdeskArticle || article_id !== helpdeskArticle.number) {
        fetchArticleResults(article_id)
      }
    }, [article_id, helpdeskArticle])

    if (!helpdeskArticle && Object.keys(article).length === 0) {
      return <Loading />
    }

    if (
      helpdeskArticle?.errors ||
      helpdeskArticle?.not_found ||
      article.errors
    ) {
      return <GenericNotFound staticContext={staticContext} />
    }

    // Handle SPA/Regular Link behaviour of navigating to different articles
    // depending on the article changing/updating
    if (article && article.article && article.article.number == article_id) {
      results = {
        article: article.article,
        related_articles: article.related_articles,
      }
    } else if (helpdeskArticle && helpdeskArticle.number == article_id) {
      results = {
        article: helpdeskArticle,
        related_articles: helpdeskRelatedArticles,
      }
    } else if (helpdeskArticle && helpdeskArticle.not_found) {
      results = helpdeskArticle
    } else {
      results = { article: {}, related_articles: [] }
    }

    if (results.not_found) {
      return <GenericNotFound staticContext={staticContext} />
    }

    return <WrappedComponent {...props} results={results} />
  }

  InternalFetchArticle.propTypes = {
    article: PropTypes.object,
    fetchArticleResults: PropTypes.func,
    helpdeskArticle: PropTypes.object,
    helpdeskRelatedArticles: PropTypes.array,
    staticContext: PropTypes.object,
  }

  const mapStateToProps = {
    article: state.article,
  }

  const mapDispatchToProps = {
    fetchArticleResults: (articleNumber) => {
      fetchArticleResults(articleNumber, dispatch)
    },
  }

  const combineProps = {
    ...props,
    ...mapStateToProps,
    ...mapDispatchToProps,
  }
  return InternalFetchArticle(combineProps)
}

export default fetchArticle
